import { Component, Input, ViewChild } from '@angular/core';
import { CreateInvoiceObjectionComponent } from 'components/accounting/create-invoice-objection.component';
import { CreditWizardComponent } from 'components/accounting/credit-wizard/credit-wizard.component';
import { EditInvoiceDueDateComponent } from 'components/accounting/edit-invoice-duedate.component';
import { EditInvoicePaymentsComponent } from 'components/accounting/ledger/edit-invoice-payments.component';
import { SetInvoiceOnHoldComponent } from 'components/accounting/set-invoice-on-hold.component';
import { ButtonComponent } from 'components/input/button/button.component';
import { CardComponent } from 'components/layout/card/card.component';
import { TooltipComponent } from 'components/shared/tooltip/tooltip.component';
import { BcDocumentType } from 'enums/BcDocumentType';
import { CredentialType } from 'enums/CredentialType';
import { OnHoldReason } from 'enums/OnHoldReason';
import { IssuedReminderModel } from 'models/generated/BusinessCentral.Integration.Models.IssuedReminder';
import { InstallmentPaymentModel } from 'models/generated/Entities.Accounting.InstallmentPayment';
import { InvoiceObjectionModel } from 'models/generated/Entities.Accounting.InvoiceObjection';
import { ReasonModel } from 'models/generated/Entities.GormCrm.Reason';
import { Layout } from 'modules/layout';
import { ListView } from 'modules/list-view';
import { Pipes } from 'modules/pipes';
import { AccountingService } from 'services/accounting.service';
import { InvoicingService } from 'services/invoicing.service';
import { NavigationService } from 'services/navigation.service';
import { NotificationService } from 'services/notification.service';
import { ReasonService } from 'services/reason.service';
import { UserService } from 'services/user.service';
import { LedgerEntryDisplayModel } from './company-invoices.component';
import { BillingDocumentLineModel } from 'models/generated/Accounting.IntegrationAbstractions.BillingDocumentLine';
import { BrandService } from 'services/brand.service';
import { AccountingIntegrationType } from 'enums/AccountingIntegrationType';
import { firstValueFrom } from 'rxjs';

@Component({
	selector: 'ledger-entry-details',
	templateUrl: './ledger-entry-details.component.html',
	imports: [Layout, ListView, CardComponent, TooltipComponent, Pipes]
})
export class LedgerEntryDetailsComponent {

	@ViewChild('sendButton') sendButton?: ButtonComponent;

	bcDocumentType = BcDocumentType;
	credentialType = CredentialType;
	public lines?: BillingDocumentLineModel[];
	public totalValue: number = 0;
	public objectionReasons = new Map<string, ReasonModel>();
	public loading = true;
	private suspendedAccountingPlans = true;
	private integrationType?: AccountingIntegrationType;

	private _ledgerEntry?: LedgerEntryDisplayModel;
	@Input() companyId!: string;
	@Input() documentId?: string;
	@Input() farpayInvoiceId?: number;
	@Input() installmentPayments?: InstallmentPaymentModel[];
	@Input() objection?: InvoiceObjectionModel;
	@Input() issuedReminder?: IssuedReminderModel;
	@Input()
	set ledgerEntry(entry: LedgerEntryDisplayModel) {
		this.lines = undefined;
		this.loadEntryData(entry);
	}
	get ledgerEntry() { return this._ledgerEntry!; }
	get ledgerHeader() { return this._ledgerEntry?.ledgerHeader; }
	get isInvoice() { return this._ledgerEntry?.documentType == BcDocumentType.Invoice; }

	constructor(private readonly invoicingService: InvoicingService,
		private readonly userService: UserService,
		private readonly accountingService: AccountingService,
		private readonly navigation: NavigationService,
		private readonly notificationService: NotificationService,
		private readonly reasonService: ReasonService,
		private readonly brandService: BrandService) {
	}

	get documentAvailableForDownload() {
		return !!this.documentId || !!this.farpayInvoiceId;
	}
	get documentAvailableForResend() {
		return (this.documentId && this.integrationType == AccountingIntegrationType.BusinessCentral) || !!this.farpayInvoiceId;
	}

	get vatValue() { return this.totalValueInclVat - this.totalValue; }
	get totalValueInclVat() { return AccountingService.addVat(this.totalValue); }
	get currentUserId() { return this.userService.getCurrentUser().id; }
	get canEditInstallmentPayments() { return !this.ledgerHeader || !this.ledgerHeader.onHoldReason || this.ledgerHeader.onHoldReason == OnHoldReason.PaymentArrangementAgreed; }
	get validForDueDateUpdate() { return this.isInvoice && this._ledgerEntry?.open; }

	async reloadEntry() {
		const brandId = this.ledgerEntry.brandId!;
		const [entries, ledgerHeaders] = await Promise.all([
			this.invoicingService.getCustomerLedgerEntriesByDocumentNos([this.ledgerEntry.documentNo], brandId),
			this.accountingService.getLedgerHeadersByPostedDocumentNumbers([this.ledgerEntry.documentNo], brandId)
		]);
		if (!entries || entries.length == 0) throw Error('Entry not found');

		this._ledgerEntry = entries[0];
		this._ledgerEntry.brandId = brandId;
		this._ledgerEntry.ledgerHeader = ledgerHeaders.find(lh => lh);
		await this.loadEntryData(this.ledgerEntry);
	}

	async loadEntryData(entry: LedgerEntryDisplayModel) {
		this.brandService.getAccountingSystemIntegrationType(entry.brandId).then(type => this.integrationType = type);
		this.totalValue = entry.amount ?? 0;
		this.suspendedAccountingPlans = entry.ledgerHeader?.ledgerPosts.some(lp => lp.accountingPlan?.suspended) ?? false;
		this._ledgerEntry = entry;

		this.loading = true;

		const promises = [
			this.reasonService.getReasonMap().then(reasonMap => { this.objectionReasons = reasonMap; }),
			this.getDocumentLines(entry.documentNo, entry.documentType, entry.brandId!)
		];

		Promise.all(promises).then(() => {
			this.loading = false;
		});
	}

	async getDocumentLines(postedNo: string, type: BcDocumentType, brandId: string) {
		this.lines = await this.invoicingService.getPostedDocumentLines(postedNo, type, brandId);
		this.totalValue = this.sumAmounts(this.lines?.map(line => line.totalNetAmount ?? 0) ?? []);
	}
	
	private sumAmounts(amounts: number[]): number { return amounts.reduce((partialSum, a) => partialSum + a, 0); }

	async createCreditMemo() {
		if (!this.lines || this.totalValue <= 0 || !this.isInvoice || !this.ledgerEntry.brandId) return;
		const view = await this.navigation.openPopup(CreditWizardComponent, 'invoice.create-credit-memo.popup-header');
		view.sourceData = { invoiceLines: this.lines, invoiceNumber: this.ledgerEntry.documentNo, brandId: this.ledgerEntry.brandId, companyId: this.companyId };
	}

	createEditInvoiceObjection() {
		this.navigation.openPopup(CreateInvoiceObjectionComponent, 'invoice.create-objection.popup-header').then(view => {
			view.documentNo = this.ledgerEntry.documentNo;
			view.brandId = this.ledgerEntry.brandId;
			view.objection = Object.assign(new InvoiceObjectionModel, this.objection);
			view.objection.companyId = this.companyId;
			view.objectionSaved.subscribe(objection => {
				this.objection = objection;
			});
		});
	}

	editInvoiceDueDate() {
		if (!this.validForDueDateUpdate) return;
		this.navigation.openPopup(EditInvoiceDueDateComponent, 'invoice.edit-invoice-duedate.popup-header').then(view => {
			view.dueDate = this.ledgerEntry.dueDate!;
			view.brandId = this.ledgerEntry.brandId!;
			view.documentNo = this.ledgerEntry.documentNo;
		});
	}

	async editInstallmentPayments() {
		if (!this.ledgerEntry.remainingAmountInclVat) return;

		const view = await this.navigation.openPopup(EditInvoicePaymentsComponent, 'invoice.edit-invoice-payments.popup-header');
		view.documentNo = this.ledgerEntry.documentNo;
		view.brandId = this.ledgerEntry.brandId;
		view.existingPayments = this.installmentPayments ?? [];
		view.remainingAmount = this.ledgerEntry.remainingAmountInclVat;
	}

	async toggleOnHold() {
		if (this.ledgerHeader?.onHoldReason) {
			if (this.suspendedAccountingPlans) {
				this.notificationService.warning('invoice.remove-on-hold-suspended-accounting-plan');
				return;
			}
			this.loading = true;
			await this.invoicingService.updateOnHold(this.ledgerEntry.brandId!, this.ledgerEntry.documentNo, this.companyId, undefined);
			await this.reloadEntry();
		}
		else {

			this.navigation.openPopup(SetInvoiceOnHoldComponent, 'invoice.set-on-hold.popup-header').then(view => {
				view.companyId = this.companyId;
				view.brandId = this.ledgerEntry.brandId;
				view.documentNo = this.ledgerEntry.documentNo;
				view.statusChanged.subscribe(async () => {
					await this.reloadEntry();
				});
			});
		}
	}

	public onHoldReason(reasonType?: OnHoldReason): string {
		if (!reasonType) return 'unknown';
		return OnHoldReason.getKeys().get(reasonType) ?? 'unknown';
	}

	public async sendDocument() {
		if (!this.documentAvailableForResend) return;
		const notificationDescription = this.ledgerEntry.documentType === BcDocumentType.Invoice ? 'ledger-entry-details.send-invoice.confirm-description' : 'ledger-entry-details.send-credit-memo.confirm-description';
		const notificationTitle = this.ledgerEntry.documentType === BcDocumentType.Invoice ? 'ledger-entry-details.send-invoice.confirm-title' : 'ledger-entry-details.send-credit-memo.confirm-title';
		const result = await firstValueFrom(this.notificationService.confirm(notificationDescription, { documentNumber: this.ledgerEntry.documentNo }, notificationTitle));

		if (!result) {
			this.sendButton?.reset();
			return;
		}
		if (this.documentId && this.integrationType == AccountingIntegrationType.BusinessCentral) {

			// If there's an available Document ID, and the invoice is in Business Central, we can resend the document using BC
			if (this.ledgerEntry.documentType === BcDocumentType.Invoice) {
				await this.invoicingService.sendSalesInvoice(this.documentId, this.ledgerEntry.brandId!);
			}
			else if (this.ledgerEntry.documentType === BcDocumentType.CreditMemo) {
				await this.invoicingService.sendCreditMemo(this.documentId, this.ledgerEntry.brandId!);
			}
			else {
				this.notificationService.warning('ledger-entry-details.send-document.unsupported-document-type');
				this.sendButton?.reset();
				return;
			}
		}
		else if (this.farpayInvoiceId) {

			// Otherwise, use Farpay to resend the invoice (if the invoice has a valid Farpay ID)
			await this.accountingService.sendFarPayInvoice(this.farpayInvoiceId, this.ledgerEntry.brandId!);
		}
		else throw new Error('Missing information required to resend invoice'); // Shouldn't be reachable, due to the documentAvailableForResend check

		this.notificationService.success('ledger-entry-details.send-document.success');
		this.sendButton?.succeeded();
	}

	public downloadDocument() {
		if (!this.documentAvailableForDownload) return;
		const filename = `${this.ledgerEntry.documentNo}.pdf`;
		if (this.ledgerEntry.documentType === BcDocumentType.Invoice && this.documentId) 
			this.invoicingService.downloadSalesInvoice(this.documentId, filename, this.ledgerEntry.brandId!);
		else if (this.ledgerEntry.documentType === BcDocumentType.CreditMemo && this.documentId) 
			this.invoicingService.downloadCreditMemo(this.documentId, filename, this.ledgerEntry.brandId!);
		else if (this.farpayInvoiceId)
			this.accountingService.downloadFarPayInvoice(this.farpayInvoiceId, filename, this.ledgerEntry.brandId!);
		else {
			this.notificationService.warning('ledger-entry-details.download-document.unsupported-document-type');
			return;
		}
	}
}
